import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Loading: React.FC = () => {
  const navigate = useNavigate();
  const { formData } = useFormContext();
  const [showCheckmark, setShowCheckmark] = React.useState(false);

  useEffect(() => {
    // Validate that we have the necessary data
    if (!formData.email) {
      navigate('/email');
      return;
    }

    // Show checkmark after validation
    const validationTimer = setTimeout(() => {
      setShowCheckmark(true);
    }, 5000);

    // Navigate to payment page after showing checkmark
    // Use replace: true to skip /loading in browser history
    const navigationTimer = setTimeout(() => {
      navigate('/payment', { replace: true });
    }, 6000);

    return () => {
      clearTimeout(validationTimer);
      clearTimeout(navigationTimer);
    };
  }, [navigate, formData.email]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <div className="form-container fade-in">
          {/* Main Heading */}
          <h2 className="text-2xl md:text-3xl text-center font-bold text-prussian-blue mb-6">
            Einen Moment bitte...
          </h2>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-center font-semibold text-gray-800 mb-8">
            {!showCheckmark
              ? 'Wir prüfen, ob mit Ihren Angaben die Melderegisteranfrage ausgeführt werden kann.'
              : 'Ihre Angaben wurden erfolgreich geprüft!'
            }
          </p>

          {/* Loading Spinner or Checkmark */}
          <div className="flex justify-center mb-8">
            {!showCheckmark ? (
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 border-8 border-gray-200 rounded-full"></div>
                <div
                  className="absolute inset-0 border-8 border-transparent rounded-full animate-spin"
                  style={{
                    borderTopColor: '#003a5d',
                    borderRightColor: '#003a5d'
                  }}
                ></div>
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center animate-in zoom-in duration-300"
                style={{ backgroundColor: '#32936F' }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Customer Rating */}
          <div className="bg-gray-50 p-5 md:p-6 rounded-lg mb-6 border border-gray-200">
            <div className="flex justify-center items-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="#FFD700"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-center text-base md:text-lg text-gray-700">
              Das Urteil unserer Kunden: <strong>HERVORRAGEND 4.7 von 5 Sternen</strong>
            </p>
          </div>

          {/* Security Message */}
          <p
            className="text-center mb-4 flex items-center justify-center gap-2 text-base md:text-lg"
            style={{
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            <span className="inline-block w-4 h-4">🔒</span>
            Ihre Daten werden streng vertraulich behandelt und nicht an Dritte weitergegeben.
          </p>

          {/* Security Badges */}
          <div className="flex justify-center items-center gap-6 mb-6 px-4">
            <div className="flex justify-center items-center gap-6 flex-nowrap">
              {/* SSL Badge */}
              <div className="flex overflow-hidden" style={{ width: '140px', height: '55px', minWidth: '140px' }}>
                <img
                  src="/images/ssl-badge.svg"
                  alt="SSL Sichere Verbindung"
                  className="w-full h-full"
                  style={{
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              </div>

              {/* DSGVO Badge */}
              <div className="flex rounded-lg overflow-hidden" style={{ width: '140px', height: '55px', minWidth: '140px' }}>
                <img
                  src="/images/dsgvo-badge.svg"
                  alt="DSGVO"
                  className="w-full h-full"
                  style={{
                    objectFit: 'contain',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-6"></div>

          {/* German Registry Info */}
          <div className="flex items-center justify-center gap-3 max-w-xl mx-auto px-4">
            <div className="flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 5 3"
                className="w-8 h-auto"
                style={{
                  border: '1px solid #eaeaea',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}
              >
                <rect width="5" height="3" fill="#000" />
                <rect width="5" height="2" y="1" fill="#D00" />
                <rect width="5" height="1" y="2" fill="#FFCE00" />
              </svg>
            </div>
            <span
              className="text-base md:text-lg"
              style={{
                color: '#000',
                fontWeight: 400,
                lineHeight: 1.35,
                textAlign: 'center'
              }}
            >
              Sie erhalten die zuverlässige Adressauskunft direkt aus den Registern der zuständigen Einwohnermeldeämter.
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Loading;

